<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500 hover:bg-gray-50"
                                        @click="goBack"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">
                                                {{ pdp?.name || 'Loading...' }}
                                        </h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">PDP ID: {{ routeId }}</p>
                                </div>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading PDP...</span>
                </div>

                <!-- Main Content -->
                <main v-else class="p-6">
                        <!-- Tabs -->
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <!-- Tab Headers -->
                                <div class="flex border-b border-gray-200 overflow-x-auto">
                                        <button
                                                v-for="tab in tabs"
                                                :key="tab.id"
                                                type="button"
                                                class="py-4 px-6 text-sm font-medium whitespace-nowrap border-none bg-transparent cursor-pointer border-b-2 -mb-px"
                                                :class="activeTab === tab.id ? 'text-orange-7 border-orange-7 bg-orange-1' : 'text-gray-500 border-transparent'"
                                                @click="activeTab = tab.id"
                                        >
                                                {{ tab.label }}
                                        </button>
                                </div>

                                <!-- Tab Content -->
                                <div class="p-6">
                                        <!-- Properties Tab -->
                                        <div v-if="activeTab === 'properties'">
                                                <h3 class="text-base font-semibold text-gray-900 m-0 mb-4">PDP Properties</h3>
                                                <div class="max-w-lg">
                                                        <div class="mb-4">
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                                <div class="flex gap-2">
                                                                        <input
                                                                                v-model="pdpName"
                                                                                type="text"
                                                                                class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                        >
                                                                        <button
                                                                                type="button"
                                                                                :disabled="isSavingName || pdpName === pdp?.name"
                                                                                class="py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50"
                                                                                @click="saveName"
                                                                        >
                                                                                {{ isSavingName ? 'Saving...' : 'Save' }}
                                                                        </button>
                                                                </div>
                                                        </div>
                                                        <div class="mb-4">
                                                                <label class="flex items-center gap-2 cursor-pointer">
                                                                        <input
                                                                                v-model="addOfferingImage"
                                                                                type="checkbox"
                                                                                class="w-4 h-4 accent-orange-7"
                                                                                @change="saveAddOfferingImage"
                                                                        >
                                                                        <span class="text-sm text-gray-700">Add Offering Image</span>
                                                                </label>
                                                                <p class="text-xs text-gray-500 mt-1 ml-6">When enabled, offering images will be added to this PDP</p>
                                                        </div>
                                                </div>

                                                <!-- Translations Section -->
                                                <div class="mt-8 pt-6 border-t border-gray-200">
                                                <div class="flex justify-between items-center mb-4">
                                                        <h3 class="text-base font-semibold text-gray-900 m-0">Translations</h3>
                                                        <div class="flex items-center gap-2">
                                                                <span
                                                                        v-if="hasUnsavedTranslations"
                                                                        class="text-xs bg-amber-100 text-amber-700 py-1 px-2 rounded-full font-medium"
                                                                >
                                                                        Unsaved changes
                                                                </span>
                                                                <button
                                                                        type="button"
                                                                        :disabled="!hasUnsavedTranslations || isSavingTranslations"
                                                                        class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-6 transition-colors"
                                                                        @click="saveTranslations"
                                                                >
                                                                        <svg v-if="isSavingTranslations" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                                        </svg>
                                                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                        {{ isSavingTranslations ? 'Saving...' : 'Save Changes' }}
                                                                </button>
                                                        </div>
                                                </div>

                                                <!-- Loading translations -->
                                                <div v-if="isLoadingTranslations" class="flex justify-center items-center py-8">
                                                        <svg class="w-8 h-8 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                        </svg>
                                                        <span class="ml-3 text-gray-500 text-sm">Loading translations...</span>
                                                </div>

                                                <template v-else-if="languages.length > 0">
                                                        <!-- Language Tabs -->
                                                        <div class="flex items-center gap-1 mb-4 border-b border-gray-200 pb-3">
                                                                <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                                                </svg>
                                                                <button
                                                                        v-for="lang in languages"
                                                                        :key="lang.id"
                                                                        type="button"
                                                                        class="py-1.5 px-3 text-sm font-medium rounded-md transition-colors border-none cursor-pointer"
                                                                        :class="selectedLanguage === lang.id
                                                                                ? 'bg-orange-7 text-white'
                                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
                                                                        @click="selectedLanguage = lang.id"
                                                                >
                                                                        {{ lang.name }}
                                                                </button>
                                                        </div>

                                                        <!-- Translation Fields for Selected Language -->
                                                        <div v-if="translations[selectedLanguage]" class="max-w-2xl space-y-4">
                                                                <div>
                                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Header</label>
                                                                        <input
                                                                                v-model="translations[selectedLanguage].header"
                                                                                type="text"
                                                                                placeholder="Enter header text..."
                                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                                                @input="markTranslationsChanged"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                                                        <textarea
                                                                                v-model="translations[selectedLanguage].description"
                                                                                rows="4"
                                                                                placeholder="Enter description..."
                                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none resize-y focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                                                @input="markTranslationsChanged"
                                                                        ></textarea>
                                                                </div>
                                                                <div>
                                                                        <label class="block text-sm font-medium text-gray-700 mb-2">About</label>
                                                                        <textarea
                                                                                v-model="translations[selectedLanguage].about"
                                                                                rows="4"
                                                                                placeholder="Enter about text..."
                                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none resize-y focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                                                @input="markTranslationsChanged"
                                                                        ></textarea>
                                                                </div>
                                                        </div>
                                                </template>

                                                <div v-else class="text-center py-8 px-4 bg-gray-50 rounded-lg">
                                                        <p class="text-gray-500 text-sm m-0">No languages configured</p>
                                                </div>
                                                </div>
                                        </div>

                                        <!-- Images Tab -->
                                        <div v-if="activeTab === 'images'">
                                                <div class="flex justify-between items-center mb-4">
                                                        <h3 class="text-base font-semibold text-gray-900 m-0">PDP Images</h3>
                                                        <button
                                                                type="button"
                                                                class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer hover:bg-orange-6"
                                                                @click="showImageModal = true"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                                </svg>
                                                                Add Image
                                                        </button>
                                                </div>
                                                <div v-if="pdpImages.length === 0" class="text-center py-8 px-4 bg-gray-50 rounded-lg">
                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        <p class="text-gray-500 text-sm m-0">No PDP images yet</p>
                                                </div>
                                                <div v-else class="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
                                                        <div
                                                                v-for="image in pdpImages"
                                                                :key="image.id"
                                                                class="bg-gray-50 rounded-lg p-4 relative"
                                                        >
                                                                <img
                                                                        :src="image.url"
                                                                        alt="PDP Image"
                                                                        class="w-full h-32 object-contain rounded-md mb-2"
                                                                >
                                                                <div class="text-xs text-gray-500">
                                                                        <div v-if="image.languageid">Language: {{ getLanguageName(image.languageid) }}</div>
                                                                        <div>Size: {{ image.width }}x{{ image.height }}</div>
                                                                </div>
                                                                <div class="absolute top-2 right-2 flex gap-1">
                                                                        <button
                                                                                type="button"
                                                                                class="p-1 bg-white border border-gray-200 rounded cursor-pointer text-gray-500 hover:bg-gray-50"
                                                                                title="Edit"
                                                                                @click="editImage(image)"
                                                                        >
                                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                </svg>
                                                                        </button>
                                                                        <button
                                                                                type="button"
                                                                                class="p-1 bg-white border border-gray-200 rounded cursor-pointer text-red-600 hover:bg-red-50"
                                                                                title="Delete"
                                                                                @click="deleteImage(image)"
                                                                        >
                                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                </svg>
                                                                        </button>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Image Modal -->
                <div v-if="showImageModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="closeImageModal"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">
                                                {{ editingImage ? 'Edit PDP Image' : 'Add PDP Image' }}
                                        </h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1 hover:text-gray-600"
                                                @click="closeImageModal"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <!-- Language -->
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Language (optional)</label>
                                                <select
                                                        v-model="imageForm.languageid"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                >
                                                        <option :value="null">None</option>
                                                        <option v-for="lang in languages" :key="lang.id" :value="lang.id">
                                                                {{ lang.id }} | {{ lang.name }}
                                                        </option>
                                                </select>
                                        </div>
                                        <!-- Width -->
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Width (px)</label>
                                                <input
                                                        v-model.number="imageForm.width"
                                                        type="number"
                                                        placeholder="Enter width"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                >
                                        </div>
                                        <!-- Height -->
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Height (px)</label>
                                                <input
                                                        v-model.number="imageForm.height"
                                                        type="number"
                                                        placeholder="Enter height"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                >
                                        </div>
                                        <!-- Image Upload -->
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Image</label>
                                                <button
                                                        type="button"
                                                        class="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg bg-transparent cursor-pointer text-center text-gray-500 hover:border-orange-7 hover:bg-orange-1"
                                                        @click="openFilePicker"
                                                >
                                                        <svg class="w-6 h-6 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        Click to upload image
                                                </button>
                                                <div v-if="imageForm.url" class="mt-3 flex items-center gap-3 p-2 bg-gray-50 rounded-md">
                                                        <img :src="imageForm.url" alt="Preview" class="w-16 h-16 object-contain rounded">
                                                        <div class="flex-1 text-xs text-gray-500 overflow-hidden text-ellipsis">
                                                                {{ imageForm.url }}
                                                        </div>
                                                        <button
                                                                type="button"
                                                                class="text-red-600 bg-transparent border-none cursor-pointer p-1 hover:bg-red-50 rounded"
                                                                @click="imageForm.url = ''"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
                                                @click="closeImageModal"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                :disabled="!imageForm.url || isSavingImage"
                                                class="py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 hover:bg-orange-6"
                                                @click="saveImage"
                                        >
                                                {{ isSavingImage ? 'Saving...' : 'Save' }}
                                        </button>
                                </div>
                        </div>
                </div>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                class="flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                        </div>
                </div>
        </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onUnmounted, reactive } from 'vue';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import Swal from 'sweetalert2';
import { useAuth0 } from '@auth0/auth0-vue';
import PDPService from 'services/PDPService';
import PDPImageService from 'services/PDPImageService';
import LanguageService from 'services/LanguageService';
import TranslationService from 'services/TranslationService';
import { httpClient } from 'utils/http';
import { showDeleteConfirmation } from 'utils/swal';

export default defineComponent({
        name: 'EditPDPPage',
        setup() {
                const router = useRouter();
                const route = useRoute();
                const auth0 = useAuth0();

                const routeId = computed(() => route.params.id);

                const pdp = ref(null);
                const pdpName = ref('');
                const addOfferingImage = ref(false);
                const pdpImages = ref([]);
                const languages = ref([]);
                const isLoading = ref(true);
                const isSavingName = ref(false);
                const isSavingImage = ref(false);

                const activeTab = ref('properties');
                const tabs = [
                        { id: 'properties', label: 'Properties' },
                        { id: 'images', label: 'Images' },
                ];

                // Translations state
                const translations = ref({});
                const selectedLanguage = ref('');
                const isLoadingTranslations = ref(false);
                const isSavingTranslations = ref(false);
                const hasUnsavedTranslations = ref(false);

                const showImageModal = ref(false);
                const editingImage = ref(null);
                const imageForm = reactive({
                        languageid: null,
                        width: 0,
                        height: 0,
                        url: '',
                });

                const toastMessage = ref('');
                const toastVariant = ref('success');
                let toastTimeout;

                const showToast = (message, variant = 'success') => {
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 3000);
                };

                const getLanguageName = (id) => {
                        const lang = languages.value.find(l => l.id === id);
                        return lang ? lang.name : `Language ${id}`;
                };

                const goBack = () => {
                        router.push('/pdp');
                };

                const loadPdp = async () => {
                        isLoading.value = true;
                        try {
                                pdp.value = await PDPService.get(routeId.value);
                                pdpName.value = pdp.value.name || '';
                                addOfferingImage.value = pdp.value.addOfferingImage === 1 || pdp.value.addOfferingImage === true;
                        } catch (error) {
                                showToast(error.message || 'Failed to load PDP', 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };

                const loadPdpImages = async () => {
                        try {
                                const params = new URLSearchParams({
                                        where: JSON.stringify({ pdpid: routeId.value }),
                                        limit: '0',
                                });
                                pdpImages.value = await PDPImageService.getAll(params);
                        } catch (error) {
                                showToast(error.message || 'Failed to load PDP images', 'error');
                        }
                };

                const loadLanguages = async () => {
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                languages.value = await LanguageService.getAll(params);
                                if (languages.value.length > 0) {
                                        const defaultLang = languages.value.find(l => l.default);
                                        selectedLanguage.value = defaultLang ? defaultLang.id : languages.value[0].id;
                                }
                        } catch (error) {
                                console.error('Failed to load languages:', error);
                        }
                };

                const loadTranslations = async () => {
                        if (languages.value.length === 0) return;

                        isLoadingTranslations.value = true;
                        try {
                                const langIds = languages.value.map(l => l.id).join(' ');
                                const params = new URLSearchParams({
                                        ns: 'pdps',
                                        lng: langIds,
                                        limit: '0',
                                });
                                const data = await TranslationService.getMultiTranslation(params);

                                const newTranslations = {};
                                languages.value.forEach(lang => {
                                        const langData = data[lang.id]?.pdps?.[routeId.value];
                                        newTranslations[lang.id] = {
                                                header: langData?.header || '',
                                                description: langData?.description || '',
                                                about: langData?.about || '',
                                        };
                                });
                                translations.value = newTranslations;
                                hasUnsavedTranslations.value = false;
                        } catch (error) {
                                console.error('loadTranslations error:', error);
                                showToast(error.message || 'Failed to load translations', 'error');
                        } finally {
                                isLoadingTranslations.value = false;
                        }
                };

                const markTranslationsChanged = () => {
                        hasUnsavedTranslations.value = true;
                };

                const saveTranslations = async () => {
                        isSavingTranslations.value = true;
                        try {
                                // Save each language separately
                                for (const lang of languages.value) {
                                        if (translations.value[lang.id]) {
                                                const bundle = {
                                                        [routeId.value]: {
                                                                header: translations.value[lang.id].header || '',
                                                                description: translations.value[lang.id].description || '',
                                                                about: translations.value[lang.id].about || '',
                                                        },
                                                };

                                                await httpClient.put('/api/translation', {
                                                        language: lang.id,
                                                        namespace: 'pdps',
                                                        bundle,
                                                });
                                        }
                                }

                                hasUnsavedTranslations.value = false;
                                showToast('Translations saved successfully');
                        } catch (error) {
                                showToast(error.message || 'Failed to save translations', 'error');
                        } finally {
                                isSavingTranslations.value = false;
                        }
                };

                const saveName = async () => {
                        if (!pdpName.value.trim() || pdpName.value === pdp.value?.name) return;
                        
                        isSavingName.value = true;
                        try {
                                await PDPService.update(routeId.value, { name: pdpName.value.trim() });
                                pdp.value.name = pdpName.value.trim();
                                showToast('PDP name updated successfully');
                        } catch (error) {
                                showToast(error.message || 'Failed to update PDP name', 'error');
                        } finally {
                                isSavingName.value = false;
                        }
                };

                const saveAddOfferingImage = async () => {
                        try {
                                await PDPService.update(routeId.value, { addOfferingImage: addOfferingImage.value ? 1 : 0 });
                                showToast('Setting updated successfully');
                        } catch (error) {
                                showToast(error.message || 'Failed to update setting', 'error');
                                addOfferingImage.value = !addOfferingImage.value;
                        }
                };

                const openFilePicker = () => {
                        if (window.$client) {
                                window.$client.picker({
                                        fromSources: ['local_file_system'],
                                        onUploadDone: (files) => {
                                                if (files.filesUploaded && files.filesUploaded.length > 0) {
                                                        imageForm.url = files.filesUploaded[0].url;
                                                }
                                        },
                                }).open();
                        } else {
                                const url = prompt('Enter image URL:');
                                if (url) {
                                        imageForm.url = url;
                                }
                        }
                };

                const closeImageModal = () => {
                        showImageModal.value = false;
                        editingImage.value = null;
                        imageForm.languageid = null;
                        imageForm.width = 0;
                        imageForm.height = 0;
                        imageForm.url = '';
                };

                const editImage = (image) => {
                        editingImage.value = image;
                        imageForm.languageid = image.languageid;
                        imageForm.width = image.width;
                        imageForm.height = image.height;
                        imageForm.url = image.url;
                        showImageModal.value = true;
                };

                const saveImage = async () => {
                        if (!imageForm.url) return;

                        isSavingImage.value = true;
                        try {
                                if (editingImage.value) {
                                        await PDPImageService.update(editingImage.value.id, {
                                                languageid: imageForm.languageid,
                                                width: imageForm.width || 0,
                                                height: imageForm.height || 0,
                                                url: imageForm.url,
                                        });
                                        showToast('PDP image updated successfully');
                                } else {
                                        await PDPImageService.create({
                                                pdpid: parseInt(routeId.value, 10),
                                                languageid: imageForm.languageid,
                                                width: imageForm.width || 0,
                                                height: imageForm.height || 0,
                                                url: imageForm.url,
                                        });
                                        showToast('PDP image added successfully');
                                }
                                await loadPdpImages();
                                closeImageModal();
                        } catch (error) {
                                showToast(error.message || 'Failed to save PDP image', 'error');
                        } finally {
                                isSavingImage.value = false;
                        }
                };

                const deleteImage = async (image) => {
                        const confirmed = await showDeleteConfirmation(
                                'Delete PDP Image',
                                'Are you sure you want to delete this PDP image?'
                        );

                        if (confirmed) {
                                try {
                                        await PDPImageService.delete(image.id);
                                        pdpImages.value = pdpImages.value.filter(i => i.id !== image.id);
                                        showToast('PDP image deleted successfully');
                                } catch (error) {
                                        showToast(error.message || 'Failed to delete PDP image', 'error');
                                }
                        }
                };

                watch(activeTab, async (newTab) => {
                        if (newTab === 'images' && pdpImages.value.length === 0) {
                                await loadLanguages();
                                loadPdpImages();
                        }
                        if (newTab === 'translations' && Object.keys(translations.value).length === 0) {
                                await loadLanguages();
                                await loadTranslations();
                        }
                });

                const loggedIn = computed(() => auth0.isAuthenticated.value);

                watch(loggedIn, (isLoggedIn) => {
                        if (isLoggedIn) {
                                loadPdp();
                        }
                });

                // Browser beforeunload handler for tab close/external navigation
                const handleBeforeUnload = (e) => {
                        if (hasUnsavedTranslations.value) {
                                e.preventDefault();
                                e.returnValue = '';
                                return '';
                        }
                };

                onMounted(async () => {
                        window.addEventListener('beforeunload', handleBeforeUnload);
                        if (auth0.isAuthenticated.value) {
                                loadPdp();
                                await loadLanguages();
                                await loadTranslations();
                        }
                });

                onUnmounted(() => {
                        window.removeEventListener('beforeunload', handleBeforeUnload);
                });

                // Vue Router navigation guard
                onBeforeRouteLeave(async (to, from, next) => {
                        if (hasUnsavedTranslations.value) {
                                const result = await Swal.fire({
                                        title: 'Unsaved Changes',
                                        text: 'You have unsaved changes. Are you sure you want to leave this page?',
                                        icon: 'warning',
                                        showCancelButton: true,
                                        confirmButtonColor: '#FF6600',
                                        cancelButtonColor: '#6b7280',
                                        confirmButtonText: 'Leave Page',
                                        cancelButtonText: 'Stay',
                                });
                                if (result.isConfirmed) {
                                        next();
                                } else {
                                        next(false);
                                }
                        } else {
                                next();
                        }
                });

                return {
                        routeId,
                        pdp,
                        pdpName,
                        addOfferingImage,
                        pdpImages,
                        languages,
                        isLoading,
                        isSavingName,
                        isSavingImage,
                        activeTab,
                        tabs,
                        translations,
                        selectedLanguage,
                        isLoadingTranslations,
                        isSavingTranslations,
                        hasUnsavedTranslations,
                        showImageModal,
                        editingImage,
                        imageForm,
                        toastMessage,
                        toastVariant,
                        getLanguageName,
                        goBack,
                        saveName,
                        saveAddOfferingImage,
                        markTranslationsChanged,
                        saveTranslations,
                        openFilePicker,
                        closeImageModal,
                        editImage,
                        saveImage,
                        deleteImage,
                };
        },
});
</script>

<style>
@keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
}
</style>
